import { useEffect, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

export interface AppManifest {
    name: string;
    version: string;
}

interface ManifestResponse {
    [appName: string]: AppManifest;
}

const STORAGE_KEY = 'app-manifests';
const POLL_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

export function getEnvironmentUrl(): string {
    const hostname = window.location.hostname;

    if (
        hostname.includes('localhost') ||
        hostname.includes('127.0.0.1') ||
        hostname.includes('development')
    ) {
        return 'https://storage.googleapis.com/mpt-staging-assets-cdn/moniebook-dev';
    }

    if (hostname.includes('staging')) {
        return 'https://storage.googleapis.com/mpt-staging-assets-cdn/moniebook-staging';
    }

    if (hostname.includes('beta') || hostname.includes('beta-')) {
        return 'https://cdn-prod.moniepoint.com/moniebook-beta';
    }

    return 'https://cdn-prod.moniepoint.com/moniebook';
}

async function fetchManifests(): Promise<ManifestResponse> {
    const cdnBaseUrl = getEnvironmentUrl();
    const apps = [
        'auth-ui',
        'settings-ui',
        'business-mgt-ui',
        'dashboard-ui',
        'accounting-ui',
        'customers-ui',
        'inventory-onboarding-ui',
        'onboarding-ui',
        'orders-ui',
        'registers-ui',
        'reports-ui',
        'sale-settings-ui',
        'products-ui',
    ];

    const hostManifestPromise = fetch('/manifest.json').then((response) =>
        response.json(),
    );

    const remoteManifestPromises = apps.map((app) =>
        fetch(`${cdnBaseUrl}/${app}/manifest.json`).then((response) =>
            response.json(),
        ),
    );

    const results = await Promise.allSettled([
        hostManifestPromise,
        ...remoteManifestPromises,
    ]);

    const manifests: ManifestResponse = {
        'host-ui': { name: 'host-ui', version: '' },
    };

    if (results[0].status === 'fulfilled') {
        manifests['host-ui'] = (
            results[0] as PromiseFulfilledResult<AppManifest>
        ).value;
    }

    for (let i = 1; i < results.length; i++) {
        const result = results[i];

        if (result.status === 'fulfilled') {
            const manifest = (result as PromiseFulfilledResult<AppManifest>)
                .value;
            manifests[manifest.name] = manifest;
        }
    }

    return manifests;
}

function storedManifestsMatch(manifests: ManifestResponse): boolean {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);

        if (!stored) return true;

        const storedManifests = JSON.parse(stored) as ManifestResponse;

        return JSON.stringify(manifests) === JSON.stringify(storedManifests);
    } catch {
        return false;
    }
}

function storeManifests(manifests: ManifestResponse): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(manifests));
    } catch (error) {
        console.error('Failed to store manifests in localStorage:', error);
    }
}

export function useManifestPoller(
    onUpdateDetected: ((manifests: ManifestResponse) => void) | undefined,
): void {
    const hasShownToastRef = useRef(false);

    const { data: manifests } = useQuery({
        queryKey: ['app-manifests'],
        queryFn: fetchManifests,
        staleTime: POLL_INTERVAL_MS,
        refetchInterval: POLL_INTERVAL_MS,
        refetchOnWindowFocus: false,
        enabled: onUpdateDetected !== undefined,
    });

    useEffect(() => {
        if (!manifests || !onUpdateDetected) return;

        const isSameVersion = storedManifestsMatch(manifests);
        storeManifests(manifests);

        if (!isSameVersion && !hasShownToastRef.current) {
            hasShownToastRef.current = true;
            toast.info(
                'Updates available! Will apply when you navigate to a new page.',
            );
            onUpdateDetected(manifests);
        } else if (isSameVersion) {
            hasShownToastRef.current = false;
        }
    }, [manifests, onUpdateDetected]);
}
